import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Footprints, Plus, X, Loader2, Check } from 'lucide-react';

/** Saisie manuelle de l'activité physique (pas + kcal brûlées) — pas de podomètre natif en web app. */
export const ActivityWidget = ({ steps, stepsGoal, kcalBurned, onAdd, en }) => {
  const [open, setOpen] = useState(false);
  const [stepsInput, setStepsInput] = useState('');
  const [kcalInput, setKcalInput] = useState('');
  const [busy, setBusy] = useState(false);
  const pct = Math.min(100, Math.round(((steps || 0) / (stepsGoal || 8000)) * 100));

  const submit = async () => {
    const s = Number(stepsInput) || 0;
    const k = Number(kcalInput) || 0;
    if (!s && !k) return;
    setBusy(true);
    try { await onAdd(s, k); setStepsInput(''); setKcalInput(''); setOpen(false); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-3xl bg-white/[0.035] backdrop-blur-xl border border-white/10 shadow-[0_0_30px_-12px_rgba(217,70,239,0.25)] p-5" data-testid="activity-widget">
      <div className="flex items-center gap-2 mb-3">
        <Footprints className="w-5 h-5 text-emerald-400" />
        <h3 className="font-bold text-white">{en ? 'Physical activity' : 'Activité physique'}</h3>
      </div>
      <div className="flex items-end justify-between mb-2">
        <p className="text-2xl font-black text-white tabular-nums">{(steps || 0).toLocaleString('fr-CH')} <span className="text-sm font-semibold text-zinc-500">/ {(stepsGoal || 8000).toLocaleString('fr-CH')} {en ? 'steps' : 'pas'}</span></p>
        <p className="text-sm font-bold text-orange-400 tabular-nums">{kcalBurned || 0} kcal</p>
      </div>
      <div className="h-2 rounded-full bg-white/8 overflow-hidden mb-4">
        <motion.div className="h-full rounded-full bg-emerald-500" initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 1, ease: 'easeOut' }} />
      </div>

      <AnimatePresence mode="wait">
        {open ? (
          <motion.div key="form" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="space-y-2" data-testid="activity-form">
            <div className="flex gap-2">
              <input type="number" min="0" value={stepsInput} onChange={(e) => setStepsInput(e.target.value)} placeholder={en ? 'Steps' : 'Pas'} className="flex-1 min-w-0 w-full bg-zinc-800 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm focus:border-primary focus:outline-none" data-testid="activity-steps-input" />
              <input type="number" min="0" value={kcalInput} onChange={(e) => setKcalInput(e.target.value)} placeholder="Kcal" className="flex-1 min-w-0 w-full bg-zinc-800 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm focus:border-primary focus:outline-none" data-testid="activity-kcal-input" />
            </div>
            <div className="flex gap-2">
              <button onClick={() => setOpen(false)} className="flex-1 py-2.5 rounded-xl border border-white/15 text-zinc-400 hover:text-white text-sm font-semibold transition-colors" data-testid="activity-cancel-btn"><X className="w-4 h-4 inline mr-1" />{en ? 'Cancel' : 'Annuler'}</button>
              <button onClick={submit} disabled={busy} className="flex-1 py-2.5 rounded-xl font-bold text-white bg-gradient-to-r from-primary to-pink-500 disabled:opacity-60 flex items-center justify-center gap-1.5 text-sm" data-testid="activity-submit-btn">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} {en ? 'Add' : 'Ajouter'}
              </button>
            </div>
          </motion.div>
        ) : (
          <motion.button key="btn" onClick={() => setOpen(true)} className="w-full py-3 rounded-2xl bg-white/5 hover:bg-white/10 font-medium text-sm text-zinc-300 flex items-center justify-center gap-1.5 transition-colors" data-testid="activity-add-btn">
            <Plus className="w-4 h-4" /> {en ? 'Add an activity' : 'Ajouter une activité'}
          </motion.button>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ActivityWidget;
